import { useState } from 'react';
import { useReportBuilderStore } from '../../stores/reportBuilderStore';
import type { WindowFuncType, WindowFunctionSpec, SortSpec } from '../../types/reportBuilder';
import { WINDOW_FUNC_LABELS } from '../../types/reportBuilder';
import { X, Plus, ArrowUp, ArrowDown } from '@phosphor-icons/react';
import { pillClasses } from './fieldEncoding';

const WINDOW_FUNC_OPTIONS: WindowFuncType[] = ['rank', 'dense_rank', 'row_number', 'lag', 'lead', 'running_sum'];
const NEEDS_COLUMN: WindowFuncType[] = ['lag', 'lead', 'running_sum'];

export default function WindowFunctionBuilder({ onClose }: { onClose: () => void }) {
  const { selectedMeasures, selectedDimensions, addWindowFunction } = useReportBuilderStore();

  const [funcType, setFuncType] = useState<WindowFuncType>('rank');
  const [column, setColumn] = useState(selectedMeasures[0]?.alias ?? '');
  const [partitionBy, setPartitionBy] = useState<string[]>([]);
  const [orderBy, setOrderBy] = useState<SortSpec[]>([{ column: selectedMeasures[0]?.alias ?? '', direction: 'desc' }]);
  const [lagOffset, setLagOffset] = useState(1);

  // Available columns for partition/order: dimensions + measure aliases
  const availableCols = [
    ...selectedDimensions.map(d => ({ key: d, label: d })),
    ...selectedMeasures.map(m => ({ key: m.alias, label: m.alias })),
  ];

  const handleAddOrderBy = () => {
    const firstAvail = availableCols[0];
    if (firstAvail) setOrderBy([...orderBy, { column: firstAvail.key, direction: 'asc' }]);
  };

  const handleSave = () => {
    const key = `wf_${funcType}_${Date.now()}`;
    const label = WINDOW_FUNC_LABELS[funcType] + (NEEDS_COLUMN.includes(funcType) ? ` (${column})` : '');
    const wf: WindowFunctionSpec = {
      key,
      label,
      function: funcType,
      column: NEEDS_COLUMN.includes(funcType) ? column : '',
      partition_by: partitionBy.length > 0 ? partitionBy : undefined,
      order_by: orderBy,
    };
    if (funcType === 'lag' || funcType === 'lead') {
      wf.params = { offset: lagOffset };
    }
    addWindowFunction(wf);
    onClose();
  };

  const isValid = orderBy.length > 0 && orderBy.every(ob => ob.column) && (!NEEDS_COLUMN.includes(funcType) || column);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="w-[420px] rounded-lg bg-white shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-800">Add Window Function</h3>
          <button onClick={onClose} className="rounded p-1 hover:bg-gray-100"><X size={14} /></button>
        </div>

        <div className="space-y-3 px-4 py-3">
          {/* Function type */}
          <div>
            <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Function</label>
            <select value={funcType} onChange={e => setFuncType(e.target.value as WindowFuncType)}
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none">
              {WINDOW_FUNC_OPTIONS.map(f => <option key={f} value={f}>{WINDOW_FUNC_LABELS[f]}</option>)}
            </select>
          </div>

          {/* Column (for lag/lead/running_sum) */}
          {NEEDS_COLUMN.includes(funcType) && (
            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Column / Measure</label>
              <select value={column} onChange={e => setColumn(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none">
                {availableCols.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
          )}

          {/* Lag/Lead offset */}
          {(funcType === 'lag' || funcType === 'lead') && (
            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Offset</label>
              <input type="number" min={1} value={lagOffset} onChange={e => setLagOffset(Math.max(1, Number(e.target.value)))}
                className="w-24 rounded border border-gray-300 px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none" />
            </div>
          )}

          {/* Partition By */}
          <div>
            <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Partition By (optional)</label>
            <div className="flex flex-wrap gap-1">
              {selectedDimensions.map(d => (
                <button key={d} onClick={() => setPartitionBy(prev => prev.includes(d) ? prev.filter(p => p !== d) : [...prev, d])}
                  className={`rounded border px-2 py-0.5 text-[10px] ${partitionBy.includes(d) ? pillClasses('dimension') : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                  {d}
                </button>
              ))}
              {selectedDimensions.length === 0 && <span className="text-[10px] text-gray-400 italic">No dimensions selected</span>}
            </div>
          </div>

          {/* Order By */}
          <div>
            <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Order By</label>
            <div className="space-y-1">
              {orderBy.map((ob, i) => (
                <div key={i} className="flex items-center gap-1">
                  <select value={ob.column} onChange={e => {
                    const updated = [...orderBy];
                    updated[i] = { ...ob, column: e.target.value };
                    setOrderBy(updated);
                  }} className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none">
                    {availableCols.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                  <button onClick={() => {
                    const updated = [...orderBy];
                    updated[i] = { ...ob, direction: ob.direction === 'asc' ? 'desc' : 'asc' };
                    setOrderBy(updated);
                  }} className="rounded border border-gray-200 p-1 text-gray-500 hover:bg-gray-50" title={ob.direction}>
                    {ob.direction === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
                  </button>
                  {orderBy.length > 1 && (
                    <button onClick={() => setOrderBy(orderBy.filter((_, j) => j !== i))}
                      className="rounded p-1 text-gray-400 hover:bg-gray-100"><X size={10} /></button>
                  )}
                </div>
              ))}
              {availableCols.length > 0 && (
                <button onClick={handleAddOrderBy}
                  className="flex items-center gap-0.5 text-[10px] text-blue-600 hover:text-blue-800">
                  <Plus size={10} /> Add order column
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t px-4 py-3">
          <button onClick={onClose} className="rounded border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={!isValid}
            className="rounded bg-primary px-3 py-1.5 text-xs text-white hover:bg-primary/90 disabled:opacity-50">
            Add
          </button>
        </div>
      </div>
    </div>
  );
}
